import React from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useEscapeKey } from '../shared/useGlobalModalEscape';

export type NotificationType = 'error' | 'success' | 'warning' | 'info';

interface AppDialogModalProps {
  isOpen: boolean;
  type?: NotificationType;
  title: string;
  message: string;
  confirmText?: string;
  onClose: () => void;
}

export const AppDialogModal: React.FC<AppDialogModalProps> = ({
  isOpen,
  type = 'info',
  title,
  message,
  confirmText = 'OK',
  onClose,
}) => {
  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'error':
        return <AlertCircle size={28} color="#ef4444" />;
      case 'success':
        return <CheckCircle size={28} color="#10b981" />;
      case 'warning':
        return <AlertTriangle size={28} color="#f59e0b" />;
      case 'info':
      default:
        return <Info size={28} color="#3b82f6" />;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'error': return '#fecaca';
      case 'success': return '#a7f3d0';
      case 'warning': return '#fde68a';
      case 'info': return '#bfdbfe';
    }
  };

  const getHeaderBg = () => {
    switch (type) {
      case 'error': return '#fef2f2';
      case 'success': return '#ecfdf5';
      case 'warning': return '#fffbeb';
      case 'info': return '#eff6ff';
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }} onClick={onClose}>
      <div
        className="modal-card modal-sm"
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: `1px solid ${getBorderColor()}`,
          animation: 'fadeIn 0.15s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-header"
          style={{
            backgroundColor: getHeaderBg(),
            padding: '18px 24px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {getIcon()}
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ padding: '24px', backgroundColor: '#ffffff' }}>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#334155', whiteSpace: 'pre-wrap' }}>
            {message}
          </p>
        </div>

        <div
          style={{
            padding: '14px 24px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            className="btn-primary"
            style={{
              padding: '8px 22px',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor: type === 'error' ? '#ef4444' : undefined,
              borderColor: type === 'error' ? '#dc2626' : undefined,
            }}
            onClick={onClose}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
